/**
 * RadiografiaPanel — vista de LÍNEA DE TIEMPO legible de un lead (§20).
 *
 * Reusa la cáscara Dialog (como DebugAssignmentsPanel) pero el render es NUEVO:
 * un timeline humano (no JSON crudo). Pega a GET /leads/{id}/radiografia al abrir.
 *
 * Props: leadId, open, onClose, developmentName.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Activity, MessageSquare, Phone, Settings, Loader2, ChevronDown, ChevronRight, User, CheckCircle2, XCircle } from 'lucide-react';

// Estado de la cascada → etiqueta en español.
const ESTADO_CASCADA = {
  watching: 'Vigilando',
  claimed: 'En proceso',
  completed: 'Completado',
  cancelled: 'Cancelado',
  failed: 'Falló',
};

// Ícono + color por tipo de evento.
const EVENTO_META = {
  mensaje: { Icon: MessageSquare, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  llamada: { Icon: Phone, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  decision_motor: { Icon: Settings, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  entrega_ok: { Icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },   // Fase 3: entregado/leído
  entrega_fail: { Icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },               // Fase 3: no entregado
};

// Fecha ISO → "20 abr 13:16" en hora de México (legible, no ISO crudo).
const fmtFecha = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'America/Mexico_City', hour12: false,
    });
  } catch {
    return String(iso);
  }
};

function EventoRow({ ev, isLast }) {
  const [open, setOpen] = useState(false);
  const meta = EVENTO_META[ev.tipo] || EVENTO_META.mensaje;
  const Icon = meta.Icon;
  const tieneDetalle = ev.detalle && Object.keys(ev.detalle).length > 0;
  return (
    <div className="flex gap-3" data-testid="radiografia-evento">
      <div className="flex flex-col items-center">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${meta.bg}`}>
          <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-white/10 my-1" />}
      </div>
      <div className="flex-1 min-w-0 pb-4">
        <span className="text-[10px] text-muted-foreground">{fmtFecha(ev.timestamp)}</span>
        <p className="text-xs font-semibold text-white mt-0.5">{ev.titulo}</p>
        {ev.razon_clara && <p className="text-xs text-zinc-300 mt-0.5 break-words">{ev.razon_clara}</p>}
        {tieneDetalle && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[10px] text-muted-foreground hover:text-white mt-1 flex items-center gap-1"
          >
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {open ? 'Ocultar detalle' : 'Ver detalle'}
          </button>
        )}
        {open && tieneDetalle && (
          <pre className="bg-black/40 rounded-md p-2 text-[10px] text-zinc-400 overflow-x-auto mt-1 whitespace-pre-wrap break-words">
            {JSON.stringify(ev.detalle, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function RadiografiaPanel({ leadId, open, onClose, developmentName }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [orden, setOrden] = useState('asc');

  useEffect(() => {
    if (!open || !leadId) return undefined;
    let active = true;
    setLoading(true);
    setData(null);
    api.get(`/leads/${leadId}/radiografia`, { params: { orden } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, leadId, orden]);

  const resumen = data?.lead_resumen;
  const cascada = data?.cascada_estado;
  const timeline = data?.timeline || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="radiografia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Activity className="w-4 h-4 text-fuchsia-400" />
            Radiografía del lead
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : !data ? (
          <p className="text-xs text-muted-foreground py-10 text-center">No se pudo cargar la radiografía. Inténtalo de nuevo.</p>
        ) : (
          <div className="space-y-4">
            {resumen && (
              <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-1" data-testid="radiografia-resumen">
                <p className="text-sm font-medium text-white">{resumen.name || 'Lead sin nombre'}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                  {resumen.phone && <span>{resumen.phone}</span>}
                  {resumen.stage && <span>Etapa: {resumen.stage}</span>}
                  {resumen.assigned_to?.name && (
                    <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" />{resumen.assigned_to.name}</span>
                  )}
                  {resumen.source && <span>Origen: {resumen.source}</span>}
                  {resumen.campaign_name && <span>Campaña: {resumen.campaign_name}</span>}
                  {developmentName && <span>Desarrollo: {developmentName}</span>}
                </div>
              </div>
            )}

            {cascada && (
              <div className="rounded-lg bg-violet-500/5 border border-violet-500/20 p-3" data-testid="radiografia-cascada">
                <p className="text-[11px] font-semibold text-violet-200 flex items-center gap-1"><Settings className="w-3 h-3" />Estado del seguimiento</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-300 mt-1">
                  <span>Paso {(cascada.current_step ?? 0) + 1} de {cascada.total_steps}</span>
                  <span>Estado: {ESTADO_CASCADA[cascada.status] || cascada.status}</span>
                  {cascada.next_check_at && <span>Próxima revisión: {fmtFecha(cascada.next_check_at)}</span>}
                </div>
              </div>
            )}

            {timeline.length > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-white">Cronología ({timeline.length})</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] text-muted-foreground hover:text-white"
                  onClick={() => setOrden((o) => (o === 'asc' ? 'desc' : 'asc'))}
                  data-testid="radiografia-orden-toggle"
                >
                  {orden === 'asc' ? 'Más antiguo primero' : 'Más reciente primero'}
                </Button>
              </div>
            )}

            {timeline.length === 0 ? (
              <p className="text-xs text-muted-foreground py-8 text-center" data-testid="radiografia-vacio">
                Sin actividad registrada para este lead todavía.
              </p>
            ) : (
              <div data-testid="radiografia-timeline">
                {timeline.map((ev, i) => (
                  <EventoRow key={i} ev={ev} isLast={i === timeline.length - 1} />
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
